import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { styled } from "@mui/material";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import CustomTextArea from "../../../../customs/textarea";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from "../../../../providers/notification";

interface ScoreTypeData {
  Name: number;
  ScoreTypeId: string;
}
interface CreateScorePayload {
  ScoreId:number;
  Score: number;
  ScoreTypeId: number;
  Description: string;
  ScoreTypeName:string;
}
interface ComponentProps {
  ScoreId: number;
  description: string;
  Score: number;
  ScoreTypeId: number;
  handleClose: () => void;
}

const EditScoreContent: React.FC<ComponentProps> = ({
  ScoreId,
  handleClose,
  description,
  Score,
  ScoreTypeId,
}) => {
 
  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  const GetAllScoreTypes = async (): Promise<any> => {
    return await apiCall().get(`/SetUp/GetAllScoreTypes`);
  };

  const { data, isLoading, isError, error } = useQuery(
    ["get-AllScoreTypes"],
    GetAllScoreTypes,
    {
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const scoreTypeOption =
    data?.data?.Data?.length > 0 ? (
      data?.data?.Data.map((scoretypedata: ScoreTypeData) => (
        <option
          value={scoretypedata?.ScoreTypeId}
          key={scoretypedata?.ScoreTypeId}
        >
          {scoretypedata?.Name}
        </option>
      ))
    ) : (
      <option disabled>
        {isLoading ? "loading...." : isError ? "error" : ""}
      </option>
    );

  const createScore = async (payload: CreateScorePayload) => {
    return await apiCall().post("/SetUp/CreateUpdateScore", payload);
  };

  const createScoreMutation = useMutation(["create-score"], createScore, {
    retry: 0,
  });

  const createScoreHandler = async (
    values: FormikValues,
    resetForm: () => void
  ) => {
    const createScorePayload: CreateScorePayload = {
      ScoreId:ScoreId,
      Score: values.score,
      ScoreTypeId: values.scoreType,
      Description: values.description,
      ScoreTypeName:''
    };

    try {
      await createScoreMutation.mutateAsync(createScorePayload, {
        onSuccess: () => {
          showNotification({
            message: "Score created successful",
            type: "success",
          });
          queryClient.refetchQueries(["get-AllScore"]);
          handleClose();
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error?.message,
        type: "error",
      });
    }
  };

  const validationSchema = Yup.object().shape({
    score: Yup.number().required("Score is required"),
    scoreType: Yup.string().required("Score type is required"),
    description: Yup.string().required("Description is required"),
  });

  const formik = useFormik<FormikValues>({
    initialValues: {
      scoreType: ScoreTypeId ? ScoreTypeId : "",
      score: Score ? Score : "",
      description: description ? description : "",
    },
    onSubmit: (values, { resetForm }) => {
      createScoreHandler(values, resetForm);
    },
    validationSchema,
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <FormWrapper>
          <Field
            name="scoreType"
            type="text"
            as={Select}
            label="Score Type"
            options={scoreTypeOption}
            placeholder="Select"
          />
          <Field
            name="score"
            type="number"
            as={Input}
            label="Score"
            placeholder="Score"
          />
          <Field
            name="description"
            type="text"
            as={CustomTextArea}
            label="Description"
            placeholder="Enter Description"
          />
        </FormWrapper>
        <ButtonWrapper>
          <ButtonContainer>
            <StyledButton text="Cancel" onClick={handleClose} />
            <StyledButton
              disabled={createScoreMutation.isLoading}
              text={createScoreMutation.isLoading ? "Updating..." : "Update"}
              onClick={formik.handleSubmit}
              type="submit"
            />
          </ButtonContainer>
        </ButtonWrapper>
      </form>
    </FormikProvider>
  );
};

export default EditScoreContent;

const FormWrapper = styled("div")`
  margin-inline: 3.5rem;
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-block: 2.5rem;
  padding-inline: 3.5rem;
`;

const ButtonContainer = styled("div")`
  display: flex;
  gap: 2rem;
`;

const StyledButton = styled(Button)`
  padding-block: 1rem;
  padding-inline: 2rem;
`;
